import React, { useEffect } from "react";
import { Field, reduxForm } from "redux-form";
import { renderRadioField } from "../../../utils/renderField";
import { required } from "../../../utils/formValidators";
import {
  isAmountPastDueBetweenBalanceAndPayoff,
  removeNonNumericCharacters,
} from "../../../utils/utils";
import { AuthPaymentContainer } from "../misc/auth-payment-container.component";
import { AuthAmountField } from "./field-components/general/auth-amount-field.component";
import { AuthRestartButton } from "../misc/restart-button.component";
import { AuthContinue } from "../misc/auth-continue.component";
import {
  isExcessPayment,
  determinePayoffInfoClosedEnd,
  determinePayoffInfoOpenEnd,
  determinePayoffInfoEscrow,
  ninetyDaysOverdueMessage,
  principalOnlyMessage,
  determineOneTimePaymentsByline,
  creditCardPaymentScheduledTodayMessage,
  generateLoanDescription,
} from "../misc/messaging-utils";
import InternalSkipAccounts from "./field-components/general/internal-skip-accounts.component";
import { formatOlbStyleAuthAmountValueFromBackend } from "../misc/auth-amount-utility";

const AuthPaymentInternalTransferForm = (props) => {
  const hasSyncErrors = props.thisForm && props.thisForm.syncErrors;
  const amount = props.thisForm && props.thisForm.values && props.thisForm.values.Amount;

  const isPayoff = props.hasSelectedPayoff || props.amountPastDueBetweenPayoffAndBalance;
  const hasEscrow = props.selectedLoan.HasEscrowBalance;

  const isCreditCard = props.selectedLoan.IsCreditOrExternalAccount;

  const validInternalAccounts =
    props.selectedLoan.Is90DaysOverdue && props.selectedLoan.AmountPastDue
      ? props.internalAccounts.filter(
          (account) =>
            account.AvailableBalance >= props.selectedLoan.AmountPastDue
        )
      : props.internalAccounts.filter(
          (account) => account.AvailableBalance > 0
        );

  const shouldShowStandardMessaging = () => {
    return !props.isPrincipalOnly && !props.hasSelectedPayoff && !isCreditCard;
  };

  let payoffObject = {};

  // Only check for payoff messages/payoff button disabling if
  // this is a non-credit card, non-principal only payment
  if (!isCreditCard && !props.isPrincipalOnly) {
    if (hasEscrow) {
      payoffObject = determinePayoffInfoEscrow(amount, props.PayoffAmount);
    } else {
      payoffObject = props.selectedLoan.IsOpenEndedLoan
        ? determinePayoffInfoOpenEnd(amount, props.PayoffAmount, props.PrincipalBalance)
        : determinePayoffInfoClosedEnd(
            amount,
            props.PrincipalBalance,
            props.TenDayPayoffAmount,
            props.amountPastDueBetweenPayoffAndBalance,
          );
    }
  }

  const excessMessage = isExcessPayment(
    amount,
    props.selectedLoan.AmountDue,
    props.PrincipalBalance,
  );

  const amountIsPastDueIsBetweenBalanceAndPayoff = isAmountPastDueBetweenBalanceAndPayoff(
    props.selectedLoan.AmountPastDue,
    props.PayoffAmount,
    props.selectedLoan.CurrentBalance,
  );

  const bylinePastDue = amountIsPastDueIsBetweenBalanceAndPayoff
    ? props.PayoffAmount
    : props.selectedLoan.AmountPastDue;

  const byline = determineOneTimePaymentsByline(
    props.hasSelectedPayoff,
    bylinePastDue,
    props.PayoffAmount,
    props.selectedLoan.RegularPaymentAmount,
    props.selectedLoan.IsCreditOrExternalAccount,
    props.selectedLoan.AmountDue,
  );

  useEffect(() => {
    props.change("AccountHash", validInternalAccounts[0].AccountHash);
  }, []);

  useEffect(() => {
    if (isPayoff) {
      props.change("Amount", formatOlbStyleAuthAmountValueFromBackend(props.PayoffAmount));
    } else if (props.selectedLoan.AmountPastDue) {
      props.change(
        "Amount",
        formatOlbStyleAuthAmountValueFromBackend(props.selectedLoan.AmountPastDue),
      );
    }
  }, []);

  // On submission, send a call to the payoff check endpoint
  const submitForm = () => {
    window.scrollTo(0, 0);
    let paymentDetails = Object.assign({}, props.thisForm.values);
    paymentDetails.Amount = removeNonNumericCharacters(paymentDetails.Amount);
    paymentDetails.IsPrincipalOnly = props.isPrincipalOnly;

    // Special case where we set AchOrDebit to unique 'transfer' string
    props.callAuthCheckPayoff(paymentDetails, "transfer");
  };

  // If the call to payoff check has determined this payment is allowed,
  // send the next request
  useEffect(() => {
    if (props.authPayment.payoffCheck.PaymentIsAllowed) {
      let paymentDetails = Object.assign({}, props.thisForm.values);
      paymentDetails.Amount = removeNonNumericCharacters(paymentDetails.Amount);

      props.callAuthSaveInternalInfo(paymentDetails);
    }
  }, [props.authPayment.payoffCheck]);

  useEffect(() => {
    // If the internal response has this property, that means there were no errors
    // saving the internal transfer info and the internalResponse property has been set
    if (props.internalResponse.hasOwnProperty("AmountDue")) {
      props.setProcessStep(2);
    }
  }, [props.internalResponse]);

  return (
    <AuthPaymentContainer
      title="Payment Details"
      topChildren={[
        ninetyDaysOverdueMessage(
          props.selectedLoan.AmountPastDue,
          props.selectedLoan.Is90DaysOverdue,
          isPayoff,
          isCreditCard,
        ),
        principalOnlyMessage(props.isPrincipalOnly),
        creditCardPaymentScheduledTodayMessage(
          props.selectedLoan.IsCreditOrExternalAccount,
          props.PaymentAlreadySubmittedToday,
        ),
      ]}
    >
      <div className="bold f20">{generateLoanDescription(props.selectedLoan)}</div>
      <div className="f14">{byline}</div>
      <div className="my-4 divider"></div>
      <form onSubmit={props.handleSubmit(submitForm)}>
        <AuthAmountField
          isRecurring={false}
          isDebit={false}
          change={props.change}
          isCreditCard={isCreditCard}
          isPrincipalOnly={props.isPrincipalOnly}
          hasSelectedPayoff={isPayoff}
          currentBalance={props.selectedLoan.CurrentBalance}
          isOpenEnd={props.selectedLoan.IsOpenEndedLoan}
          payoffAmount={props.PayoffAmount}
          disabled={props.selectedLoan.Is90DaysOverdue || props.hasSelectedPayoff}
        />
        {/* Only show these messages for non-principal only, non-credit card payments */}
        {shouldShowStandardMessaging() && excessMessage}
        {shouldShowStandardMessaging() && payoffObject.component}
        <div className="mb-3">
          <div className="pt-4 mb-1 bold f18">Select Payment Account</div>
          <div className="mb-3">Your payment will come from here.</div>
          <InternalSkipAccounts
            accounts={validInternalAccounts}
            thisForm={props.thisForm}
            change={props.change}
          />
        </div>
        <AuthContinue
          title="Continue"
          type="submit"
          disabled={props.submitting || hasSyncErrors || payoffObject.disabled}
        />
        <AuthRestartButton backToLoans={props.backToLoans} />
      </form>
    </AuthPaymentContainer>
  );
};

//Connect to Redux store
export const AuthPaymentInternalTransfer = reduxForm({
  form: "AuthPaymentInternalTransfer",
  destroyOnUnmount: false,
  initialValues: {
    Amount: "",
    AccountHash: "",
  },
})(AuthPaymentInternalTransferForm);



// WEBPACK FOOTER //
// ./src/components/authenticated-payments/payment-forms/auth-payment-internal-transfer-form.component.js